import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData, MergedAmmoData } from "@/lib/types/weapon";
import type { DamageTable } from "@/lib/types/damage-table";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitTraitEffectsMap } from "@/lib/types/unit-trait-effects";
import type { TerrainSpeedModifiers } from "@/lib/types/terrain-speed-modifiers";
import { initializeModifierMaps } from "@/lib/types/veterancy-cohesion";

type UnitsData = Unit[];
type WeaponsData = { [descriptorName: string]: { descriptorName: string; hasDefensiveSmoke: boolean; weapons: WeaponData[] } };
type AmmunitionData = { [descriptorName: string]: MergedAmmoData };
type DivisionsData = Division[];

interface DataStoreContextValue {
  units: UnitsData | null;
  weapons: WeaponsData | null;
  ammunition: AmmunitionData | null;
  damageTable: DamageTable | null;
  divisions: DivisionsData | null;
  unitTraitEffects: UnitTraitEffectsMap | null;
  terrainSpeedModifiers: TerrainSpeedModifiers | null;
  unitsVersion: string | null;
  weaponsVersion: string | null;
  damageTableVersion: string | null;
  divisionsVersion: string | null;
  unitTraitEffectsVersion: string | null;
  terrainSpeedModifiersVersion: string | null;
  loadingUnits: boolean;
  loadingWeapons: boolean;
  loadingDamageTable: boolean;
  loadingDivisions: boolean;
  loadingUnitTraitEffects: boolean;
  loadingTerrainSpeedModifiers: boolean;
  errorUnits: string | null;
  errorWeapons: string | null;
  errorDamageTable: string | null;
  errorDivisions: string | null;
  errorUnitTraitEffects: string | null;
  errorTerrainSpeedModifiers: string | null;
  fetchUnits: (version?: string) => Promise<void>;
  fetchWeapons: (version?: string) => Promise<void>;
  fetchDamageTable: (version?: string) => Promise<void>;
  fetchDivisions: (version?: string) => Promise<void>;
  fetchUnitTraitEffects: (version?: string) => Promise<void>;
  fetchTerrainSpeedModifiers: (version?: string) => Promise<void>;
  getUnitByDescriptor: (descriptorName: string) => Unit | undefined;
  getWeaponByDescriptor: (descriptorName: string) => { descriptorName: string; hasDefensiveSmoke: boolean; weapons: WeaponData[] } | undefined;
  getAmmoByDescriptor: (descriptorName: string) => MergedAmmoData | undefined;
  getDivisionByDescriptor: (descriptor: string) => Division | undefined;
  hasFailedVersion: (type: "units" | "weapons" | "damage-table" | "divisions" | "unit-trait-effects" | "terrain-speed-modifiers", version: string) => boolean;
}

const DataStoreContext = createContext<DataStoreContextValue | undefined>(undefined);

export function DataStoreProvider({ children }: { children: ReactNode }) {
  const [units, setUnits] = useState<UnitsData | null>(null);
  const [weapons, setWeapons] = useState<WeaponsData | null>(null);
  const [ammunition, setAmmunition] = useState<AmmunitionData | null>(null);
  const [damageTable, setDamageTable] = useState<DamageTable | null>(null);
  const [divisions, setDivisions] = useState<DivisionsData | null>(null);
  const [unitTraitEffects, setUnitTraitEffects] = useState<UnitTraitEffectsMap | null>(null);
  const [terrainSpeedModifiers, setTerrainSpeedModifiers] = useState<TerrainSpeedModifiers | null>(null);
  const [unitsVersion, setUnitsVersion] = useState<string | null>(null);
  const [weaponsVersion, setWeaponsVersion] = useState<string | null>(null);
  const [damageTableVersion, setDamageTableVersion] = useState<string | null>(null);
  const [divisionsVersion, setDivisionsVersion] = useState<string | null>(null);
  const [unitTraitEffectsVersion, setUnitTraitEffectsVersion] = useState<string | null>(null);
  const [terrainSpeedModifiersVersion, setTerrainSpeedModifiersVersion] = useState<string | null>(null);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingWeapons, setLoadingWeapons] = useState(false);
  const [loadingDamageTable, setLoadingDamageTable] = useState(false);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingUnitTraitEffects, setLoadingUnitTraitEffects] = useState(false);
  const [loadingTerrainSpeedModifiers, setLoadingTerrainSpeedModifiers] = useState(false);
  const [errorUnits, setErrorUnits] = useState<string | null>(null);
  const [errorWeapons, setErrorWeapons] = useState<string | null>(null);
  const [errorDamageTable, setErrorDamageTable] = useState<string | null>(null);
  const [errorDivisions, setErrorDivisions] = useState<string | null>(null);
  const [errorUnitTraitEffects, setErrorUnitTraitEffects] = useState<string | null>(null);
  const [errorTerrainSpeedModifiers, setErrorTerrainSpeedModifiers] = useState<string | null>(null);
  // Track versions that have failed with 404 to prevent infinite retries
  const [failedUnitsVersions, setFailedUnitsVersions] = useState<Set<string>>(new Set());
  const [failedWeaponsVersions, setFailedWeaponsVersions] = useState<Set<string>>(new Set());
  const [failedDamageTableVersions, setFailedDamageTableVersions] = useState<Set<string>>(new Set());
  const [failedDivisionsVersions, setFailedDivisionsVersions] = useState<Set<string>>(new Set());
  const [failedUnitTraitEffectsVersions, setFailedUnitTraitEffectsVersions] = useState<Set<string>>(new Set());
  const [failedTerrainSpeedModifiersVersions, setFailedTerrainSpeedModifiersVersions] = useState<Set<string>>(new Set());
  // Track initialization to prevent double-loading in StrictMode
  const initializedRef = useRef(false);

  const fetchUnits = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (units && unitsVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedUnitsVersions.has(version)) {
      return;
    }

    setLoadingUnits(true);
    setErrorUnits(null);
    try {
      const res = await fetch(`/api/units/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedUnitsVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch units: ${res.statusText}`);
      }
      const data = await res.json();
      const unitsArray = Array.isArray(data) ? data : (data.units || []);
      setUnits(unitsArray);
      setUnitsVersion(version);
    } catch (err) {
      setErrorUnits(err instanceof Error ? err.message : "Failed to load units");
    } finally {
      setLoadingUnits(false);
    }
  }, [units, unitsVersion, failedUnitsVersions]);

  const fetchWeapons = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (weapons && weaponsVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedWeaponsVersions.has(version)) {
      return;
    }

    setLoadingWeapons(true);
    setErrorWeapons(null);
    try {
      const res = await fetch(`/api/weapons/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedWeaponsVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch weapons: ${res.statusText}`);
      }
      const data = await res.json();
      // Weapons data has structure: { ammunition: {...}, weapons: {...} }
      setWeapons(data.weapons || data);
      setAmmunition(data.ammunition || null);
      setWeaponsVersion(version);
    } catch (err) {
      setErrorWeapons(err instanceof Error ? err.message : "Failed to load weapons");
    } finally {
      setLoadingWeapons(false);
    }
  }, [weapons, weaponsVersion, failedWeaponsVersions]);

  const getUnitByDescriptor = (descriptorName: string): Unit | undefined => {
    if (!units) return undefined;
    return units.find((unit) => unit.descriptorName === descriptorName);
  };

  const fetchDamageTable = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (damageTable && damageTableVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedDamageTableVersions.has(version)) {
      return;
    }

    setLoadingDamageTable(true);
    setErrorDamageTable(null);
    try {
      const res = await fetch(`/api/damage-table/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedDamageTableVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch damage table: ${res.statusText}`);
      }
      const data = await res.json();
      setDamageTable(data);
      setDamageTableVersion(version);
    } catch (err) {
      setErrorDamageTable(err instanceof Error ? err.message : "Failed to load damage table");
    } finally {
      setLoadingDamageTable(false);
    }
  }, [damageTable, damageTableVersion, failedDamageTableVersions]);

  const getWeaponByDescriptor = (descriptorName: string) => {
    if (!weapons || !descriptorName) return undefined;
    // Remove the $/GFX/Weapon/ prefix if present, as weapons are keyed without it
    const key = descriptorName.replace(/^\$\/GFX\/Weapon\//, '');
    return weapons[key];
  };

  const getAmmoByDescriptor = (descriptorName: string): MergedAmmoData | undefined => {
    if (!ammunition || !descriptorName) return undefined;
    return ammunition[descriptorName];
  };

  const fetchDivisions = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (divisions && divisionsVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedDivisionsVersions.has(version)) {
      return;
    }

    setLoadingDivisions(true);
    setErrorDivisions(null);
    try {
      const res = await fetch(`/api/divisions/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedDivisionsVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch divisions: ${res.statusText}`);
      }
      const data = await res.json();
      const divisionsArray = Array.isArray(data) ? data : (data.divisions || []);
      setDivisions(divisionsArray);
      setDivisionsVersion(version);
    } catch (err) {
      setErrorDivisions(err instanceof Error ? err.message : "Failed to load divisions");
    } finally {
      setLoadingDivisions(false);
    }
  }, [divisions, divisionsVersion, failedDivisionsVersions]);

  const getDivisionByDescriptor = (descriptor: string): Division | undefined => {
    if (!divisions) return undefined;
    return divisions.find((division) => division.descriptor === descriptor);
  };

  const fetchUnitTraitEffects = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (unitTraitEffects && unitTraitEffectsVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedUnitTraitEffectsVersions.has(version)) {
      return;
    }

    setLoadingUnitTraitEffects(true);
    setErrorUnitTraitEffects(null);
    try {
      const res = await fetch(`/api/unit-trait-effects/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedUnitTraitEffectsVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch unit-trait-effects: ${res.statusText}`);
      }
      const data = await res.json();
      setUnitTraitEffects(data);
      setUnitTraitEffectsVersion(version);
      // Initialize modifier maps when data is loaded
      await initializeModifierMaps(data);
    } catch (err) {
      setErrorUnitTraitEffects(err instanceof Error ? err.message : "Failed to load unit-trait-effects");
    } finally {
      setLoadingUnitTraitEffects(false);
    }
  }, [unitTraitEffects, unitTraitEffectsVersion, failedUnitTraitEffectsVersions]);

  const fetchTerrainSpeedModifiers = useCallback(async (version: string = "latest") => {
    // Skip if already loaded with this version
    if (terrainSpeedModifiers && terrainSpeedModifiersVersion === version) {
      return;
    }

    // Skip if this version has already failed with 404
    if (failedTerrainSpeedModifiersVersions.has(version)) {
      return;
    }

    setLoadingTerrainSpeedModifiers(true);
    setErrorTerrainSpeedModifiers(null);
    try {
      const res = await fetch(`/api/terrain-speed-modifiers/${version}`);
      if (!res.ok) {
        // Track 404 errors to prevent infinite retries
        if (res.status === 404) {
          setFailedTerrainSpeedModifiersVersions((prev) => new Set(prev).add(version));
        }
        throw new Error(`Failed to fetch terrain-speed-modifiers: ${res.statusText}`);
      }
      const data = await res.json();
      setTerrainSpeedModifiers(data);
      setTerrainSpeedModifiersVersion(version);
    } catch (err) {
      setErrorTerrainSpeedModifiers(err instanceof Error ? err.message : "Failed to load terrain-speed-modifiers");
    } finally {
      setLoadingTerrainSpeedModifiers(false);
    }
  }, [terrainSpeedModifiers, terrainSpeedModifiersVersion, failedTerrainSpeedModifiersVersions]);

  const hasFailedVersion = useCallback((type: "units" | "weapons" | "damage-table" | "divisions" | "unit-trait-effects" | "terrain-speed-modifiers", version: string): boolean => {
    switch (type) {
      case "units":
        return failedUnitsVersions.has(version);
      case "weapons":
        return failedWeaponsVersions.has(version);
      case "damage-table":
        return failedDamageTableVersions.has(version);
      case "divisions":
        return failedDivisionsVersions.has(version);
      case "unit-trait-effects":
        return failedUnitTraitEffectsVersions.has(version);
      case "terrain-speed-modifiers":
        return failedTerrainSpeedModifiersVersions.has(version);
      default:
        return false;
    }
  }, [failedUnitsVersions, failedWeaponsVersions, failedDamageTableVersions, failedDivisionsVersions, failedUnitTraitEffectsVersions, failedTerrainSpeedModifiersVersions]);

  // Load all data at application start
  useEffect(() => {
    // Prevent double-loading in StrictMode by checking initialization ref
    if (initializedRef.current) {
      return;
    }
    
    // Mark as initialized immediately to prevent duplicate calls
    initializedRef.current = true;
    
    // Only load if data is not already loaded
    if (!units && !loadingUnits && !failedUnitsVersions.has("latest")) {
      fetchUnits("latest");
    }
    if (!weapons && !loadingWeapons && !failedWeaponsVersions.has("latest")) {
      fetchWeapons("latest");
    }
    if (!damageTable && !loadingDamageTable && !failedDamageTableVersions.has("latest")) {
      fetchDamageTable("latest");
    }
    if (!divisions && !loadingDivisions && !failedDivisionsVersions.has("latest")) {
      fetchDivisions("latest");
    }
    if (!unitTraitEffects && !loadingUnitTraitEffects && !failedUnitTraitEffectsVersions.has("latest")) {
      fetchUnitTraitEffects("latest");
    }
    if (!terrainSpeedModifiers && !loadingTerrainSpeedModifiers && !failedTerrainSpeedModifiersVersions.has("latest")) {
      fetchTerrainSpeedModifiers("latest");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return (
    <DataStoreContext.Provider
      value={{
        units,
        weapons,
        ammunition,
        damageTable,
        divisions,
        unitTraitEffects,
        terrainSpeedModifiers,
        unitsVersion,
        weaponsVersion,
        damageTableVersion,
        divisionsVersion,
        unitTraitEffectsVersion,
        terrainSpeedModifiersVersion,
        loadingUnits,
        loadingWeapons,
        loadingDamageTable,
        loadingDivisions,
        loadingUnitTraitEffects,
        loadingTerrainSpeedModifiers,
        errorUnits,
        errorWeapons,
        errorDamageTable,
        errorDivisions,
        errorUnitTraitEffects,
        errorTerrainSpeedModifiers,
        fetchUnits,
        fetchWeapons,
        fetchDamageTable,
        fetchDivisions,
        fetchUnitTraitEffects,
        fetchTerrainSpeedModifiers,
        getUnitByDescriptor,
        getWeaponByDescriptor,
        getAmmoByDescriptor,
        getDivisionByDescriptor,
        hasFailedVersion,
      }}
    >
      {children}
    </DataStoreContext.Provider>
  );
}

export function useDataStore() {
  const context = useContext(DataStoreContext);
  if (context === undefined) {
    throw new Error("useDataStore must be used within a DataStoreProvider");
  }
  return context;
}

// Convenience hooks
export function useUnitsData(version?: string) {
  const { units, loadingUnits, errorUnits, fetchUnits, hasFailedVersion } = useDataStore();
  const effectiveVersion = version || "latest";
  
  useEffect(() => {
    // Skip if this version has already failed with 404
    if (hasFailedVersion("units", effectiveVersion)) {
      return;
    }
    
    if (!units) {
      fetchUnits(version);
    } else if (version && version !== "latest") {
      // If a specific version is requested and we have different data, refetch
      fetchUnits(version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, effectiveVersion, hasFailedVersion]);

  return { units, loadingUnits, errorUnits, refetch: () => fetchUnits(version) };
}

export function useWeaponsData(version?: string) {
  const { weapons, loadingWeapons, errorWeapons, fetchWeapons, hasFailedVersion } = useDataStore();
  const effectiveVersion = version || "latest";
  
  useEffect(() => {
    // Skip if this version has already failed with 404
    if (hasFailedVersion("weapons", effectiveVersion)) {
      return;
    }
    
    if (!weapons) {
      fetchWeapons(version);
    } else if (version && version !== "latest") {
      // If a specific version is requested and we have different data, refetch
      fetchWeapons(version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, effectiveVersion, hasFailedVersion]);

  return { weapons, loadingWeapons, errorWeapons, refetch: () => fetchWeapons(version) };
}

export function useUnitByDescriptor(descriptorName: string) {
  const { getUnitByDescriptor, fetchUnits } = useDataStore();
  const unit = getUnitByDescriptor(descriptorName);

  useEffect(() => {
    if (!unit) {
      fetchUnits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptorName]);

  return unit;
}

export function useWeaponByDescriptor(descriptorName: string) {
  const { getWeaponByDescriptor, fetchWeapons } = useDataStore();
  const weapon = getWeaponByDescriptor(descriptorName);

  useEffect(() => {
    if (!weapon) {
      fetchWeapons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptorName]);

  return weapon;
}

export function useAmmoByDescriptor(descriptorName: string) {
  const { getAmmoByDescriptor, fetchWeapons } = useDataStore();
  const ammo = getAmmoByDescriptor(descriptorName);

  useEffect(() => {
    if (!ammo) {
      fetchWeapons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptorName]);

  return ammo;
}

export function useDamageTableData(version?: string) {
  const { damageTable, loadingDamageTable, errorDamageTable, fetchDamageTable, hasFailedVersion } = useDataStore();
  const effectiveVersion = version || "latest";
  
  useEffect(() => {
    // Skip if this version has already failed with 404
    if (hasFailedVersion("damage-table", effectiveVersion)) {
      return;
    }
    
    if (!damageTable) {
      fetchDamageTable(version);
    } else if (version && version !== "latest") {
      // If a specific version is requested and we have different data, refetch
      fetchDamageTable(version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, effectiveVersion, hasFailedVersion]);

  return { damageTable, loadingDamageTable, errorDamageTable, fetchDamageTable, refetch: () => fetchDamageTable(version) };
}

export function useDivisionsData(version?: string) {
  const { divisions, loadingDivisions, errorDivisions, fetchDivisions, hasFailedVersion } = useDataStore();
  const effectiveVersion = version || "latest";
  
  useEffect(() => {
    // Skip if this version has already failed with 404
    if (hasFailedVersion("divisions", effectiveVersion)) {
      return;
    }
    
    if (!divisions) {
      fetchDivisions(version);
    } else if (version && version !== "latest") {
      // If a specific version is requested and we have different data, refetch
      fetchDivisions(version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, effectiveVersion, hasFailedVersion]);

  return { divisions, loadingDivisions, errorDivisions, fetchDivisions, refetch: () => fetchDivisions(version) };
}

export function useUnitTraitEffectsData(version?: string) {
  const { unitTraitEffects, loadingUnitTraitEffects, errorUnitTraitEffects, fetchUnitTraitEffects, hasFailedVersion } = useDataStore();
  const effectiveVersion = version || "latest";
  
  useEffect(() => {
    // Skip if this version has already failed with 404
    if (hasFailedVersion("unit-trait-effects", effectiveVersion)) {
      return;
    }
    
    if (!unitTraitEffects) {
      fetchUnitTraitEffects(version);
    } else if (version && version !== "latest") {
      // If a specific version is requested and we have different data, refetch
      fetchUnitTraitEffects(version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, effectiveVersion, hasFailedVersion]);

  return { unitTraitEffects, loadingUnitTraitEffects, errorUnitTraitEffects, fetchUnitTraitEffects, refetch: () => fetchUnitTraitEffects(version) };
}

